import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Select, Space, Table, Tag, message } from 'antd';
import api from '../../api/http';
import { useAuth } from '../../context/AuthContext';

const OnboardingMaterialsPage = () => {
  const auth = useAuth();
  const canManage = auth.hasPermission(['onboarding_materials_manage']);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [scopeType, setScopeType] = useState('all');
  const [form] = Form.useForm();

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/onboarding-materials/templates');
      setList(Array.isArray(data?.list) ? data.list : []);
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    (async () => {
      try {
        const { data } = await api.get('/employees', { params: { status: '在职' } });
        setEmployees(Array.isArray(data) ? data : []);
      } catch (e) {
        setEmployees([]);
      }
    })();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setScopeType('all');
    form.setFieldsValue({ status: 'active', scope_type: 'all', scope_departments: [], scope_positions: [], scope_employee_ids: [] });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const scopeJson = row.scope_json || {};
    const nextScopeType = row.scope_type || 'all';
    setScopeType(nextScopeType);
    form.setFieldsValue({
      title: row.title,
      content: row.content,
      status: row.status || 'active',
      scope_type: nextScopeType,
      scope_departments: Array.isArray(scopeJson.departments) ? scopeJson.departments : [],
      scope_positions: Array.isArray(scopeJson.positions) ? scopeJson.positions : [],
      scope_employee_ids: Array.isArray(scopeJson.employee_ids) ? scopeJson.employee_ids : []
    });
    setModalOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      const selectedIds = values.scope_employee_ids || [];
      const selectedNames = employees
        .filter((e) => selectedIds.includes(e.id))
        .map((e) => e.name)
        .filter(Boolean);
      await api.post('/onboarding-materials/templates', {
        id: editing?.id,
        title: values.title,
        content: values.content,
        status: values.status,
        scope_type: values.scope_type,
        scope_json: {
          departments: values.scope_departments || [],
          positions: values.scope_positions || [],
          employee_ids: selectedIds,
          employee_names: selectedNames
        }
      });
      message.success('保存成功');
      setModalOpen(false);
      await loadList();
    } catch (e) {
      if (!e?.errorFields) message.error(e.response?.data?.error || e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 12 }}>入职资料</h2>
      <Card
        title="告知书拟定与修改"
        extra={
          <Space>
            <Button onClick={loadList} loading={loading}>刷新</Button>
            <Button type="primary" onClick={openCreate} disabled={!canManage}>新增告知书</Button>
          </Space>
        }
      >
        <Table
          size="small"
          loading={loading}
          rowKey="id"
          dataSource={list}
          columns={[
            { title: '标题', dataIndex: 'title' },
            {
              title: '签订范围',
              width: 240,
              render: (_, row) => {
                const scope = row.scope_type || 'all';
                const scopeJson = row.scope_json || {};
                if (scope === 'department') return `部门：${(scopeJson.departments || []).join('、') || '-'}`;
                if (scope === 'position') return `岗位：${(scopeJson.positions || []).join('、') || '-'}`;
                if (scope === 'employees') return `指定员工：${((scopeJson.employee_names || []).length || (scopeJson.employee_ids || []).length)}人`;
                return '全员';
              }
            },
            { title: '已完成', dataIndex: 'signed_total', width: 90, render: (v) => Number(v || 0) },
            { title: '未完成', dataIndex: 'unsigned_total', width: 90, render: (v) => Number(v || 0) },
            { title: '目标人数', dataIndex: 'target_total', width: 90, render: (v) => Number(v || 0) },
            { title: '状态', dataIndex: 'status', width: 100, render: (v) => <Tag color={v === 'active' ? 'green' : 'default'}>{v === 'active' ? '启用' : '停用'}</Tag> },
            { title: '更新人', dataIndex: 'updated_by_user_name', width: 140, render: (v) => v || '-' },
            { title: '更新时间', dataIndex: 'updated_at', width: 180, render: (v) => v || '-' },
            {
              title: '操作',
              width: 100,
              render: (_, row) => <Button size="small" onClick={() => openEdit(row)} disabled={!canManage}>修改</Button>
            }
          ]}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        open={modalOpen}
        title={editing ? '修改告知书' : '新增告知书'}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        confirmLoading={saving}
        okButtonProps={{ disabled: !canManage }}
        width={880}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="例如：员工入职安全告知书" />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true }]}> 
            <Select options={[{ value: 'active', label: '启用' }, { value: 'inactive', label: '停用' }]} />
          </Form.Item>
          <Form.Item name="scope_type" label="签订范围" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'all', label: '全员' },
                { value: 'department', label: '按部门' },
                { value: 'position', label: '按岗位' },
                { value: 'employees', label: '指定员工' }
              ]}
              onChange={(v) => setScopeType(v || 'all')}
            />
          </Form.Item>
          {scopeType === 'department' && (
            <Form.Item name="scope_departments" label="选择部门" rules={[{ required: true, message: '请选择部门' }]}>
              <Select
                mode="multiple"
                options={Array.from(new Set(employees.map((e) => e.department).filter(Boolean))).map((d) => ({ value: d, label: d }))}
              />
            </Form.Item>
          )}
          {scopeType === 'position' && (
            <Form.Item name="scope_positions" label="选择岗位" rules={[{ required: true, message: '请选择岗位' }]}>
              <Select
                mode="multiple"
                options={Array.from(new Set(employees.map((e) => e.position).filter(Boolean))).map((p) => ({ value: p, label: p }))}
              />
            </Form.Item>
          )}
          {scopeType === 'employees' && (
            <Form.Item name="scope_employee_ids" label="指定员工" rules={[{ required: true, message: '请选择员工' }]}>
              <Select
                mode="multiple"
                showSearch
                options={employees.map((e) => ({ value: e.id, label: `${e.name}${e.department ? `（${e.department}）` : ''}` }))}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          )}
          <Form.Item name="content" label="内容" rules={[{ required: true, message: '请输入内容' }]}>
            <Input.TextArea rows={14} placeholder="请输入告知书正文内容" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OnboardingMaterialsPage;
